import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search } from 'lucide-react';

export const COUNTRY_CODES = [
  { code: '+44', country: 'GB', flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom' },
  { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}', name: 'United States' },
  { code: '+1', country: 'CA', flag: '\u{1F1E8}\u{1F1E6}', name: 'Canada' },
  { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia' },
  { code: '+353', country: 'IE', flag: '\u{1F1EE}\u{1F1EA}', name: 'Ireland' },
  { code: '+64', country: 'NZ', flag: '\u{1F1F3}\u{1F1FF}', name: 'New Zealand' },
  { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}', name: 'India' },
  { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany' },
  { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}', name: 'France' },
  { code: '+34', country: 'ES', flag: '\u{1F1EA}\u{1F1F8}', name: 'Spain' },
  { code: '+39', country: 'IT', flag: '\u{1F1EE}\u{1F1F9}', name: 'Italy' },
  { code: '+31', country: 'NL', flag: '\u{1F1F3}\u{1F1F1}', name: 'Netherlands' },
  { code: '+46', country: 'SE', flag: '\u{1F1F8}\u{1F1EA}', name: 'Sweden' },
  { code: '+47', country: 'NO', flag: '\u{1F1F3}\u{1F1F4}', name: 'Norway' },
  { code: '+45', country: 'DK', flag: '\u{1F1E9}\u{1F1F0}', name: 'Denmark' },
  { code: '+358', country: 'FI', flag: '\u{1F1EB}\u{1F1EE}', name: 'Finland' },
  { code: '+48', country: 'PL', flag: '\u{1F1F5}\u{1F1F1}', name: 'Poland' },
  { code: '+351', country: 'PT', flag: '\u{1F1F5}\u{1F1F9}', name: 'Portugal' },
  { code: '+41', country: 'CH', flag: '\u{1F1E8}\u{1F1ED}', name: 'Switzerland' },
  { code: '+43', country: 'AT', flag: '\u{1F1E6}\u{1F1F9}', name: 'Austria' },
  { code: '+32', country: 'BE', flag: '\u{1F1E7}\u{1F1EA}', name: 'Belgium' },
  { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}', name: 'Japan' },
  { code: '+82', country: 'KR', flag: '\u{1F1F0}\u{1F1F7}', name: 'South Korea' },
  { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}', name: 'China' },
  { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}', name: 'Singapore' },
  { code: '+852', country: 'HK', flag: '\u{1F1ED}\u{1F1F0}', name: 'Hong Kong' },
  { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}', name: 'UAE' },
  { code: '+966', country: 'SA', flag: '\u{1F1F8}\u{1F1E6}', name: 'Saudi Arabia' },
  { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}', name: 'Brazil' },
  { code: '+52', country: 'MX', flag: '\u{1F1F2}\u{1F1FD}', name: 'Mexico' },
  { code: '+27', country: 'ZA', flag: '\u{1F1FF}\u{1F1E6}', name: 'South Africa' },
  { code: '+234', country: 'NG', flag: '\u{1F1F3}\u{1F1EC}', name: 'Nigeria' },
  { code: '+254', country: 'KE', flag: '\u{1F1F0}\u{1F1EA}', name: 'Kenya' },
  { code: '+20', country: 'EG', flag: '\u{1F1EA}\u{1F1EC}', name: 'Egypt' },
  { code: '+90', country: 'TR', flag: '\u{1F1F9}\u{1F1F7}', name: 'Turkey' },
  { code: '+7', country: 'RU', flag: '\u{1F1F7}\u{1F1FA}', name: 'Russia' },
  { code: '+380', country: 'UA', flag: '\u{1F1FA}\u{1F1E6}', name: 'Ukraine' },
  { code: '+60', country: 'MY', flag: '\u{1F1F2}\u{1F1FE}', name: 'Malaysia' },
  { code: '+63', country: 'PH', flag: '\u{1F1F5}\u{1F1ED}', name: 'Philippines' },
  { code: '+66', country: 'TH', flag: '\u{1F1F9}\u{1F1ED}', name: 'Thailand' },
  { code: '+84', country: 'VN', flag: '\u{1F1FB}\u{1F1F3}', name: 'Vietnam' },
  { code: '+62', country: 'ID', flag: '\u{1F1EE}\u{1F1E9}', name: 'Indonesia' },
  { code: '+92', country: 'PK', flag: '\u{1F1F5}\u{1F1F0}', name: 'Pakistan' },
  { code: '+880', country: 'BD', flag: '\u{1F1E7}\u{1F1E9}', name: 'Bangladesh' },
];

export function CountryCodePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const selected = COUNTRY_CODES.find(c => c.code === value && c.country === (value === '+1' ? 'US' : c.country)) || COUNTRY_CODES[0];
  const filtered = search
    ? COUNTRY_CODES.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.code.includes(search) || c.country.toLowerCase().includes(search.toLowerCase()))
    : COUNTRY_CODES;

  return (
    <div ref={ref} className="relative">
      <button type="button" data-testid="country-code-picker"
        onClick={() => { setOpen(!open); setSearch(''); }}
        className="flex items-center gap-1 h-full px-3 py-4 rounded-l-2xl bg-white border border-r-0 border-gray-200 text-sm text-gray-900 hover:bg-gray-50 transition-colors whitespace-nowrap">
        <span className="text-base">{selected.flag}</span>
        <span className="font-medium">{selected.code}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden">
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input ref={searchRef} data-testid="country-code-search" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search country..." className="w-full pl-8 pr-3 py-2 text-sm rounded-lg bg-gray-50 border border-gray-100 outline-none focus:border-gray-300" />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filtered.map((c, i) => (
                <button key={`${c.country}-${i}`} type="button" data-testid={`country-option-${c.country}`}
                  onClick={() => { onChange(c.code); setOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${c.code === value ? 'bg-gray-50 font-medium' : ''}`}>
                  <span className="text-base">{c.flag}</span>
                  <span className="flex-1 text-gray-900">{c.name}</span>
                  <span className="text-gray-400 text-xs">{c.code}</span>
                </button>
              ))}
              {filtered.length === 0 && <p className="px-3 py-4 text-sm text-gray-400 text-center">No results</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
